<template>
	<div class="view-as-user-banner max-w-screen">
		<div class="view-as-user-banner__placeholder p-4"></div>
		<CCallout type="info" :use-icon="false" class="view-as-user-banner__container" :custom-slot-class="'view-as-user-banner__content'">
			<div class="flex items-center">
				<span class="text-nowrap">{{ isMobile ? 'Viewing as' : 'You are viewing ' + orgName + ' as' }}</span>&nbsp;
				<CAvatar
					:avatar="displayedUserAvatar"
					class="mx-1"
				/>
				<span class="font-bold truncate">&nbsp;{{ displayedUserName }}.</span>
				<div v-if="showAdditionalText">
					&nbsp;You will not be able to save changes.
				</div>
			</div>
			<template #button>
				<button class="button text-button" @click="exitViewAsUser">
					<Inline svg="logout"></Inline>Exit
				</button>
			</template>
		</CCallout>
	</div>
</template>
<script lang="ts">
	import { defineComponent, computed, inject } from 'vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CAvatar from 'src/components/auth/Avatar.vue';
	import { isMobileKey, viewAsUserKey } from 'src/utilities/injection-keys';
	import { useRoute } from 'vue-router/composables';

	export default defineComponent({
		name: 'CViewAsUserBanner',
		components: {
			CCallout,
			CAvatar
		},
		props: {
			displayedUserName: {
				type: String,
				required: true
			},
			displayedUserAvatar: {
				type: String,
				required: true
			}
		},
		setup() {
			const route = useRoute();
			const isMobile = inject(isMobileKey);
			const viewAsUser = inject(viewAsUserKey);

			const showAdditionalText = computed(() => (route.name === 'builder' || route.name === 'publish') && !isMobile.value);

			const exitViewAsUser = async () => {
				await viewAsUser.handleExitViewAsUserMode();
			};

			return {
				orgName: viewAsUser.orgName,
				showAdditionalText,
				isMobile,
				exitViewAsUser
			};
		}
	});
</script>
<style lang="scss">
	.view-as-user-banner {
		--view-as-avatar-outline: 2px; // Make some avatars easier to see

		&__container {
			position: fixed;
			top: 0;
			z-index: $z-view-as-banner;
			width: 100%;
			max-width: 100vw;
			border-top: 0;
			border-right: 0;
			border-left: 0;
			border-color: var(--border-selected-intense);
		}

		// Placeholder for the banner on the settings pages
		&__placeholder {
			max-width: 100vw;
			height: calc(var(--spacing-avatar-sm) + calc(var(--view-as-avatar-outline) * 2));
			border-bottom: 1px solid transparent;
			box-sizing: content-box;
			visibility: hidden;
		}

		@media print {
			display: none !important;
		}

		:root:root & {
			margin-bottom: 0;
		}

		.callout__button-container {
			flex-direction: row; // Overwrite callout flex direction on small screens
			gap: var(--spacing-5);

			.view-as-user-banner__content {
				display: flex;
				flex: 1;
				min-width: 0;

				> div {
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
			}

			.callout__button-inner-container {
				flex-shrink: 0;
				white-space: nowrap;
			}
		}

		.avatar__image {
			background-color: var(--shade-4);
			margin: var(--view-as-avatar-outline) 0;
			outline: var(--view-as-avatar-outline) solid var(--shade-4);
		}
	}
</style>